import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import {
  LuHourglass,
  LuRefreshCw,
  LuDownload,
  LuFileText,
  LuShieldCheck,
  LuSettings,
  LuTimer,
  LuPackage,
  LuMoon,
  LuTurtle,
  LuArrowRight,
  LuEraser,
} from 'react-icons/lu'
import { DatePicker } from '@minetrace/ui/forms'

import { analyticsService } from '../../../api/services/analytics.service'
import type {
  BottleneckResponse,
  BottleneckSegment,
  BottleneckStatus,
  BottleneckQuery,
} from '../../../api/models/response/bottleneck'
import { useHasPermission } from '../../../auth/PermissionContext'
import {
  ReportPage,
  ReportHeader,
  ActionButton,
  FilterBar,
  FilterField,
  VerdictBanner,
  KpiGrid,
  KpiTile,
  Panel,
  ToneBadge,
  Notice,
  EmptyState,
  ReportSkeleton,
} from '../../../components/analytics/ReportKit'
import { reportAccent, TONES, table, type Tone } from '../../../components/analytics/reportTheme'
import { fonts, mineral } from '../../../theme'

/* ── Semáforo → tonos semánticos del kit ── */
const STATUS_TONE: Record<BottleneckStatus, Tone> = { Green: 'ok', Amber: 'warn', Red: 'bad' }
const STATUS_LABEL: Record<BottleneckStatus, string> = { Green: 'Fluido', Amber: 'Retraso', Red: 'Crítico' }

/* Nombre corto y explicación llana de cada tramo (por su key estable del backend). */
const SEGMENT_INFO: Record<string, { short: string; area: string; plain: string }> = {
  Transito: { short: 'Tránsito', area: 'Logística', plain: 'Del despacho en bocamina a la llegada a garita.' },
  PesajeBruto: { short: 'Pesaje bruto', area: 'Balanza', plain: 'De la recepción al pesaje del camión lleno.' },
  Laboratorio: { short: 'Muestreo y laboratorio', area: 'Calidad', plain: 'Del peso bruto al cierre de todos los ensayos.' },
  Conciliacion: { short: 'Conciliación', area: 'Comercial', plain: 'Del resultado de laboratorio al acuerdo comercial.' },
  PesajeNeto: { short: 'Pesaje neto', area: 'Balanza', plain: 'De la conciliación a la tara (peso neto).' },
  Liquidacion: { short: 'Liquidación', area: 'Administrativo', plain: 'Del peso neto a la liquidación emitida.' },
}

const segShort = (s: BottleneckSegment) => SEGMENT_INFO[s.key]?.short ?? s.name

/* ── Formato es-PE; "—" cuando no hay dato, nunca un 0 inventado ── */
function fmt(value: number | null | undefined, digits = 1): string {
  if (value == null) return '—'
  return value.toLocaleString('es-PE', { maximumFractionDigits: digits })
}

// Horas con su equivalente legible: minutos si es menos de una hora, días si pasa de 48 h.
function fmtHours(h: number | null | undefined): string {
  if (h == null) return '—'
  if (h > 0 && h < 1) return `${fmt(h * 60, 0)} min`
  if (h >= 48) return `${fmt(h / 24, 1)} d`
  return `${fmt(h, 1)} h`
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('es-PE')} ${n === 1 ? one : many}`

export default function CuellosBotellaPage() {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [data, setData] = useState<BottleneckResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [sealing, setSealing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  const canConfigure = useHasPermission('analytics.bottleneck.configure')
  const navigate = useNavigate()

  const rangeInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo)

  const query: BottleneckQuery = useMemo(
    () => ({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
    [dateFrom, dateTo],
  )

  const fetchData = useCallback(async () => {
    if (rangeInvalid) return
    setLoading(true)
    setError(null)
    try {
      const res = await analyticsService.bottleneck(query)
      setData(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [query, rangeInvalid])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const exportCsv = () => {
    if (!data) return
    const header = 'Tramo,Desde,Hasta,LeadTimePromHoras,DesviacionHoras,N,Activos,Dormidos,ILDPorc,UmbralTLHoras,ZScore,Estado'
    const num = (v: number | null) => (v == null ? '' : String(v))
    const rows = data.segments.map((s) =>
      [
        csv(s.name), csv(s.fromMilestone), csv(s.toMilestone),
        num(s.meanHours), num(s.stdDevHours), s.completedCount, s.activeCount, s.dormantCount,
        num(s.dormantIndexPercent), s.thresholdHours, num(s.zScore), s.status,
      ].join(','),
    )
    const blob = new Blob(['﻿' + [header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
    download(blob, `cuellos-botella-${data.generatedAt.slice(0, 10)}.csv`)
  }

  const exportPdf = async () => {
    if (!data) return
    setMsg(null)
    setExportingPdf(true)
    try {
      const url = analyticsService.bottleneckPdfUrl(query)
      const token = localStorage.getItem('auth_token')
      const tenantId = localStorage.getItem('tenant_id')
      const res = await fetch(url, {
        headers: { Authorization: token ? `Bearer ${token}` : '', 'X-Tenant-Id': tenantId ?? '' },
      })
      if (!res.ok) throw new Error('No se pudo generar el PDF')
      download(await res.blob(), `cuellos-botella-${data.generatedAt.slice(0, 10)}.pdf`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setExportingPdf(false)
    }
  }

  const saveSnapshot = async () => {
    setMsg(null)
    setSealing(true)
    try {
      const snap = await analyticsService.saveBottleneckSnapshot(query)
      setMsg(`Reporte sellado (SHA-256 ${snap.contentSha256.slice(0, 12)}…). Queda como evidencia inalterable.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSealing(false)
    }
  }

  return (
    <ReportPage>
      <ReportHeader
        icon={LuHourglass}
        title="Cuellos de Botella"
        subtitle="Cuánto tarda un lote en cada tramo, de la bocamina a la liquidación, y dónde se están quedando detenidos."
        actions={
          <>
            <ActionButton icon={LuRefreshCw} label="Actualizar" variant="ghost" onClick={() => void fetchData()} loading={loading} disabled={rangeInvalid} />
            {data && (
              <>
                <ActionButton icon={LuDownload} label="CSV" onClick={exportCsv} />
                {canConfigure && (
                  <ActionButton icon={LuShieldCheck} label="Sellar" onClick={() => void saveSnapshot()} loading={sealing}
                    title="Guarda una copia cifrada con sello SHA-256 como evidencia auditable" />
                )}
                <ActionButton icon={LuFileText} label="Exportar PDF" variant="primary" onClick={() => void exportPdf()} loading={exportingPdf} />
              </>
            )}
          </>
        }
      />

      <FilterBar note="Se evalúan los lotes despachados en el rango (fecha de salida de bocamina). Sin fechas: todo el histórico.">
        <FilterField label="Despacho desde">
          <DatePicker value={dateFrom} onChange={setDateFrom} maxDate={dateTo || undefined} />
        </FilterField>
        <FilterField label="Despacho hasta">
          <DatePicker value={dateTo} onChange={setDateTo} minDate={dateFrom || undefined} />
        </FilterField>
        {(dateFrom || dateTo) && (
          <ActionButton icon={LuEraser} label="Limpiar" variant="ghost" onClick={() => { setDateFrom(''); setDateTo('') }} />
        )}
      </FilterBar>

      {rangeInvalid && <Notice tone="warn">La fecha «desde» es posterior a «hasta». Corrige el rango para actualizar el reporte.</Notice>}
      {error && <Notice tone="bad">{error}</Notice>}
      {msg && <Notice tone="ok">{msg}</Notice>}

      {loading && !data && <ReportSkeleton />}

      {data && data.totalLots === 0 && (
        <Panel>
          <EmptyState title="No hay lotes despachados en este rango" hint="Amplía las fechas o quítalas para ver todo el histórico." />
        </Panel>
      )}

      {data && data.totalLots > 0 && <ReportBody data={data} canConfigure={canConfigure} onConfigure={() => navigate('/app/analitica/configuracion')} />}
    </ReportPage>
  )
}

/* ═════════════════════════ Cuerpo del reporte ═════════════════════════ */

function ReportBody({ data, canConfigure, onConfigure }: { data: BottleneckResponse; canConfigure: boolean; onConfigure: () => void }) {
  const red = data.segments.filter((s) => s.status === 'Red')
  const amber = data.segments.filter((s) => s.status === 'Amber')
  const criticalLots = data.dormantLots.filter((d) => d.exceedsDouble).length
  const slowest = data.segments.reduce<BottleneckSegment | null>(
    (acc, s) => (s.meanHours != null && (acc?.meanHours == null || s.meanHours > acc.meanHours) ? s : acc),
    null,
  )
  const dormantTone: Tone = criticalLots > 0 ? 'bad' : data.dormantLots.length > 0 ? 'warn' : 'ok'

  return (
    <>
      <Verdict data={data} red={red} amber={amber} criticalLots={criticalLots} />

      <KpiGrid>
        <KpiTile
          index={0}
          icon={LuTimer}
          tone="accent"
          label="Lead Time del proceso"
          value={data.leadTimeSegmentsWithData > 0 ? data.leadTimeTotalHours : null}
          format={(n) => fmtHours(n)}
          hint={
            data.leadTimeSegmentsWithData === 0
              ? 'Ningún lote completó aún un tramo'
              : data.leadTimeSegmentsWithData < 6
                ? `Suma de los promedios de ${data.leadTimeSegmentsWithData} de 6 tramos (el resto aún sin datos)`
                : 'Suma del tiempo promedio de los 6 tramos'
          }
        />
        <KpiTile
          index={1}
          icon={LuPackage}
          label="Lotes en proceso"
          value={data.liveLots}
          hint={`De ${plural(data.totalLots, 'lote', 'lotes')}: ${data.settledLots.toLocaleString('es-PE')} liquidados · ${data.closedLots.toLocaleString('es-PE')} rechazados o cancelados`}
        />
        <KpiTile
          index={2}
          icon={LuMoon}
          tone={dormantTone}
          label="Lotes dormidos"
          value={data.dormantLots.length}
          hint={
            data.dormantLots.length === 0
              ? 'Ningún lote supera el tiempo tolerado de su tramo'
              : `De ${data.liveLots.toLocaleString('es-PE')} en proceso superan la tolerancia${criticalLots > 0 ? ` · ${criticalLots.toLocaleString('es-PE')} más del doble` : ''}`
          }
        />
        <KpiTile
          index={3}
          icon={LuTurtle}
          label="Tramo más lento"
          value={slowest ? segShort(slowest) : null}
          hint={slowest ? `${fmtHours(slowest.meanHours)} en promedio · ${plural(slowest.completedCount, 'lote medido', 'lotes medidos')}` : 'Sin tramos completados'}
        />
      </KpiGrid>

      <ProcessChain data={data} />

      <SegmentsTable data={data} />

      {data.dormantLots.length > 0 && <DormantTable data={data} />}

      {data.inconsistentLots.length > 0 && (
        <Notice tone="neutral">
          <Text as="span" fontWeight={600}>
            {plural(data.inconsistentLots.length, 'lote vivo quedó fuera', 'lotes vivos quedaron fuera')} del conteo de «en proceso»
          </Text>{' '}
          porque su estado indica que ya pasaron un hito que no tiene fecha registrada (lotes antiguos o pasos hechos por fuera del flujo).
          No se les inventa una espera:{' '}
          {data.inconsistentLots.slice(0, 10).map((l, i) => (
            <Text as="span" key={l.shipmentRecordId} fontFamily={fonts.mono} fontSize="12px" title={l.reason}>
              {i > 0 && ', '}{l.batchCode}
            </Text>
          ))}
          {data.inconsistentLots.length > 10 && ` y ${data.inconsistentLots.length - 10} más`}.
        </Notice>
      )}

      <ThresholdsPanel data={data} canConfigure={canConfigure} onConfigure={onConfigure} />
    </>
  )
}

/* ── Veredicto: qué pasa, dónde y por qué, en una línea ── */
function Verdict({ data, red, amber, criticalLots }: {
  data: BottleneckResponse; red: BottleneckSegment[]; amber: BottleneckSegment[]; criticalLots: number
}) {
  const tone = STATUS_TONE[data.overallStatus]
  let title: string
  let subtitle: string
  if (data.overallStatus === 'Red') {
    title = `Cuello de botella en ${joinNames(red.map(segShort))}`
    subtitle = criticalLots > 0
      ? `${plural(criticalLots, 'lote lleva', 'lotes llevan')} más del doble del tiempo tolerado esperando en ${red.length === 1 ? 'ese tramo' : 'esos tramos'}.`
      : 'La espera actual supera la tolerancia y se aleja claramente de lo habitual.'
  } else if (data.overallStatus === 'Amber') {
    title = `Lotes acumulándose en ${joinNames(amber.map(segShort))}`
    subtitle = data.dormantLots.length > 0
      ? `${plural(data.dormantLots.length, 'lote supera', 'lotes superan')} el tiempo tolerado de su tramo, sin llegar al doble.`
      : 'La espera actual es más larga de lo habitual, aunque aún dentro de la tolerancia.'
  } else {
    title = 'El proceso fluye dentro de las tolerancias'
    subtitle = data.liveLots > 0
      ? 'Ningún lote en proceso supera el tiempo tolerado de su tramo.'
      : 'No hay lotes en proceso en este momento.'
  }
  return (
    <VerdictBanner
      tone={tone}
      title={title}
      subtitle={subtitle}
      meta={
        <>
          <Text fontFamily={fonts.display} fontWeight={700} fontSize="17px" color={mineral.ink}>
            {data.leadTimeSegmentsWithData > 0 ? fmtHours(data.leadTimeTotalHours) : '—'}
          </Text>
          <Text fontSize="12px" color={mineral.ink3}>Lead Time · {plural(data.totalLots, 'lote', 'lotes')}</Text>
        </>
      }
    />
  )
}

/* ── Recorrido del lote: barra de participación + tarjetas por tramo ── */
function ProcessChain({ data }: { data: BottleneckResponse }) {
  const withMean = data.segments.filter((s) => (s.meanHours ?? 0) > 0)
  const total = withMean.reduce((acc, s) => acc + (s.meanHours ?? 0), 0)

  return (
    <Panel
      index={1}
      title="Recorrido del lote"
      subtitle="Cuánto tarda en promedio cada tramo y cuántos lotes esperan hoy en él. El color indica el estado del tramo."
    >
      {total > 0 && (
        <Box mb={5}>
          <Text fontSize="12px" color={mineral.ink3} mb={2}>Dónde se va el tiempo (participación en el Lead Time)</Text>
          <Flex h="30px" borderRadius="10px" overflow="hidden" gap="2px" bg={mineral.sunk}>
            {withMean.map((s, i) => {
              const share = (s.meanHours ?? 0) / total
              const t = TONES[STATUS_TONE[s.status]]
              return (
                <motion.div
                  key={`${s.key}-${data.generatedAt}`}
                  initial={{ flexGrow: 0 }}
                  animate={{ flexGrow: share }}
                  transition={{ delay: 0.05 * i, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  style={{ flexBasis: 0, minWidth: 4, background: t.color, display: 'flex', alignItems: 'center', overflow: 'hidden' }}
                  title={`${segShort(s)}: ${fmtHours(s.meanHours)} (${fmt(share * 100, 0)} %)`}
                >
                  {share >= 0.12 && (
                    <Text px={2} fontSize="11px" fontWeight={600} color="#fff" whiteSpace="nowrap" truncate>
                      {segShort(s)} · {fmt(share * 100, 0)} %
                    </Text>
                  )}
                </motion.div>
              )
            })}
          </Flex>
        </Box>
      )}

      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }} gap={3}>
        {data.segments.map((s, i) => (
          <SegmentCard key={`${s.key}-${data.generatedAt}`} seg={s} index={i} />
        ))}
      </Grid>
    </Panel>
  )
}

function SegmentCard({ seg, index }: { seg: BottleneckSegment; index: number }) {
  const tone = STATUS_TONE[seg.status]
  const t = TONES[tone]
  const info = SEGMENT_INFO[seg.key]
  // Barra de promedio vs tolerancia: cuánto del TL consume el tiempo típico del tramo.
  const ratio = seg.meanHours != null && seg.thresholdHours > 0 ? Math.min(seg.meanHours / seg.thresholdHours, 1) : 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 * index, duration: 0.3 }}
      style={{ minWidth: 0 }}
    >
      <Box h="100%" borderRadius="14px" border={`1px solid ${mineral.line}`} bg={mineral.surface} position="relative" overflow="hidden" px={4} pt={4} pb={3.5}>
        <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={t.color} />

        <Flex align="center" justify="space-between" gap={2}>
          <Flex align="center" gap={2} minW={0}>
            <Flex w="22px" h="22px" borderRadius="full" align="center" justify="center" bg={reportAccent.soft} color={reportAccent.deep}
              fontSize="11px" fontWeight={700} flexShrink={0}>
              {seg.index}
            </Flex>
            <Text fontFamily={fonts.display} fontWeight={700} fontSize="15px" color={mineral.ink} truncate>
              {info?.short ?? seg.name}
            </Text>
          </Flex>
          <ToneBadge tone={tone}>{STATUS_LABEL[seg.status]}</ToneBadge>
        </Flex>

        <Flex align="center" gap={1.5} mt={1} fontSize="11.5px" color={mineral.ink3}>
          <Text truncate>{seg.fromMilestone}</Text>
          <LuArrowRight size={11} style={{ flexShrink: 0 }} />
          <Text truncate>{seg.toMilestone}</Text>
          {info && <Text color={mineral.ink4} flexShrink={0}>· {info.area}</Text>}
        </Flex>

        <Flex align="baseline" gap={2} mt={3}>
          <Text fontFamily={fonts.display} fontWeight={800} fontSize="24px" letterSpacing="-0.03em" color={mineral.ink} lineHeight="1">
            {fmtHours(seg.meanHours)}
          </Text>
          <Text fontSize="12px" color={mineral.ink3}>
            {seg.completedCount > 0 ? `promedio de ${plural(seg.completedCount, 'lote', 'lotes')}` : 'sin lotes que lo hayan completado'}
          </Text>
        </Flex>

        <Box mt={3} title={`El promedio consume el ${fmt(ratio * 100, 0)} % de la tolerancia (${fmtHours(seg.thresholdHours)})`}>
          <Box h="6px" borderRadius="full" bg={mineral.sunk} overflow="hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${ratio * 100}%` }}
              transition={{ delay: 0.1 + 0.04 * index, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ height: '100%', background: reportAccent.color, borderRadius: 999 }}
            />
          </Box>
          <Text fontSize="11px" color={mineral.ink3} mt={1}>Tolerancia del tramo: {fmtHours(seg.thresholdHours)}</Text>
        </Box>

        <Flex mt={3} pt={3} borderTop={`1px dashed ${mineral.line}`} align="center" justify="space-between" gap={2} fontSize="12px">
          <Text color={mineral.ink2}>
            {seg.activeCount > 0 ? <>Esperando ahora: <b>{seg.activeCount.toLocaleString('es-PE')}</b></> : 'Nadie esperando'}
          </Text>
          {seg.dormantCount > 0 && (
            <Text color={(seg.criticalCount > 0 ? TONES.bad : TONES.warn).deep} fontWeight={600}>
              {plural(seg.dormantCount, 'dormido', 'dormidos')}
            </Text>
          )}
        </Flex>
        {info && <Text fontSize="11px" color={mineral.ink4} mt={1.5}>{info.plain}</Text>}
      </Box>
    </motion.div>
  )
}

/* ── Tabla de indicadores por tramo ── */
const HEADERS: { label: string; help: string; align?: 'right' }[] = [
  { label: 'Tramo', help: 'Etapa del proceso entre dos hitos.' },
  { label: 'Promedio', help: 'Lead Time: tiempo promedio que tardaron los lotes que completaron el tramo.', align: 'right' },
  { label: 'Variación (σ)', help: 'Desviación estándar: cuánto se alejan los lotes del promedio. Alta = proceso irregular.', align: 'right' },
  { label: 'Rango', help: 'El lote más rápido y el más lento que completaron el tramo.', align: 'right' },
  { label: 'Medidos', help: 'Lotes que completaron el tramo (base del promedio).', align: 'right' },
  { label: 'Esperando', help: 'Lotes vivos que están hoy en este tramo.', align: 'right' },
  { label: 'Dormidos', help: 'Índice de Lotes Dormidos: % de los que esperan que ya superan la tolerancia.', align: 'right' },
  { label: 'Tolerancia', help: 'Tiempo máximo aceptable en el tramo (TL), configurable por la planta.', align: 'right' },
  { label: 'Z', help: 'Z-Score: cuántas desviaciones se aleja la espera actual del promedio histórico. Requiere suficientes lotes medidos.', align: 'right' },
  { label: 'Estado', help: 'Fluido, Retraso (lotes sobre la tolerancia) o Crítico (más del doble de la tolerancia).' },
]

function SegmentsTable({ data }: { data: BottleneckResponse }) {
  return (
    <Panel index={2} padded={false} title="Indicadores por tramo" subtitle="Pasa el cursor por cada encabezado para ver qué significa.">
      <Box overflowX="auto">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead>
            <tr>
              {HEADERS.map((h) => (
                <th key={h.label} title={h.help} style={{ ...table.th, textAlign: h.align ?? 'left', cursor: 'help' }}>{h.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.segments.map((s) => (
              <tr key={s.index}>
                <td style={table.td}>
                  <Text fontWeight={600} color={mineral.ink}>{segShort(s)}</Text>
                  <Text fontSize="11.5px" color={mineral.ink3}>{s.fromMilestone} → {s.toMilestone}</Text>
                </td>
                <td style={{ ...table.td, textAlign: 'right', fontWeight: 600, color: mineral.ink }}>{fmtHours(s.meanHours)}</td>
                <td style={{ ...table.td, textAlign: 'right' }}>{fmtHours(s.stdDevHours)}</td>
                <td style={{ ...table.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {s.minHours != null ? `${fmtHours(s.minHours)} – ${fmtHours(s.maxHours)}` : '—'}
                </td>
                <td style={{ ...table.td, textAlign: 'right' }}>{s.completedCount.toLocaleString('es-PE')}</td>
                <td style={{ ...table.td, textAlign: 'right' }}>
                  {s.activeCount.toLocaleString('es-PE')}
                  {s.currentLeadHours != null && (
                    <Text fontSize="11px" color={mineral.ink3} title="Permanencia media de los que esperan hoy">{fmtHours(s.currentLeadHours)} prom.</Text>
                  )}
                </td>
                <td style={{ ...table.td, textAlign: 'right' }}>
                  {s.dormantIndexPercent == null ? '—' : (
                    <Text as="span" color={s.dormantCount > 0 ? TONES.warn.deep : mineral.ink2} fontWeight={s.dormantCount > 0 ? 600 : 400}>
                      {fmt(s.dormantIndexPercent, 0)} %
                      <Text as="span" fontSize="11px" color={mineral.ink3} fontWeight={400}> ({s.dormantCount}/{s.activeCount})</Text>
                    </Text>
                  )}
                </td>
                <td style={{ ...table.td, textAlign: 'right' }}>{fmtHours(s.thresholdHours)}</td>
                <td style={{ ...table.td, textAlign: 'right' }}
                  title={
                    s.zScore != null
                      ? `σ usada: ${fmtHours(s.zSigmaHours)} (con piso mínimo para no amplificar diferencias de minutos)`
                      : !s.hasEnoughHistory
                        ? `Se necesitan al menos ${data.minSampleForZ} lotes medidos en el tramo`
                        : 'Nadie esperando en el tramo'
                  }>
                  {s.zScore != null ? fmt(s.zScore, 2) : '—'}
                </td>
                <td style={table.td}><ToneBadge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</ToneBadge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Box px={5} py={3.5} bg={mineral.surfaceAlt} fontSize="12px" color={mineral.ink3} lineHeight="1.55">
        <b>Cómo se decide el estado.</b> Un lote está <i>dormido</i> si lleva en el tramo más que la tolerancia; si lleva más del doble,
        el tramo es <i>crítico</i>. El Z-Score sólo agrava el color cuando la espera ya es importante frente a la tolerancia
        (y con al menos {data.minSampleForZ} lotes medidos), para que un histórico muy rápido no convierta cualquier espera en alarma.
      </Box>
    </Panel>
  )
}

/* ── Lotes dormidos ── */
function DormantTable({ data }: { data: BottleneckResponse }) {
  return (
    <Panel
      index={3}
      padded={false}
      title={`Lotes dormidos (${data.dormantLots.length.toLocaleString('es-PE')})`}
      subtitle="Lotes que llevan en su tramo actual más tiempo del tolerado. Ordenados por tramo y espera."
    >
      <Box overflowX="auto">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead>
            <tr>
              <th style={table.th}>Lote</th>
              <th style={table.th}>Esperando en</th>
              <th style={{ ...table.th, textAlign: 'right' }}>Lleva</th>
              <th style={{ ...table.th, textAlign: 'right' }}>Tolerancia</th>
              <th style={{ ...table.th, textAlign: 'right' }} title="Cuántas veces la tolerancia del tramo">Exceso</th>
            </tr>
          </thead>
          <tbody>
            {data.dormantLots.map((d) => {
              const seg = data.segments.find((s) => s.index === d.segmentIndex)
              const times = d.thresholdHours > 0 ? d.elapsedHours / d.thresholdHours : null
              return (
                <tr key={`${d.shipmentRecordId}-${d.segmentIndex}`}>
                  <td style={{ ...table.td, fontFamily: fonts.mono, fontSize: 12.5, color: mineral.ink }}>{d.batchCode}</td>
                  <td style={table.td}>{seg ? segShort(seg) : `Tramo ${d.segmentIndex}`}</td>
                  <td style={{ ...table.td, textAlign: 'right', fontWeight: 600, color: (d.exceedsDouble ? TONES.bad : TONES.warn).deep }}>
                    {fmtHours(d.elapsedHours)}
                  </td>
                  <td style={{ ...table.td, textAlign: 'right' }}>{fmtHours(d.thresholdHours)}</td>
                  <td style={{ ...table.td, textAlign: 'right' }}>
                    <ToneBadge tone={d.exceedsDouble ? 'bad' : 'warn'}>{times != null ? `${fmt(times, 1)}× TL` : '> TL'}</ToneBadge>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Box>
    </Panel>
  )
}

/* ── Tolerancias vigentes (solo lectura); la edición vive en Configuración de Analítica ── */
function ThresholdsPanel({ data, canConfigure, onConfigure }: { data: BottleneckResponse; canConfigure: boolean; onConfigure: () => void }) {
  return (
    <Panel
      index={4}
      title="Tolerancias por tramo"
      subtitle="Tiempo máximo aceptable en cada tramo. Superarlo marca al lote como dormido; superar el doble, como crítico."
      actions={canConfigure ? <ActionButton icon={LuSettings} label="Configurar" variant="ghost" onClick={onConfigure} /> : undefined}
    >
      <Grid templateColumns={{ base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }} gap={3}>
        {data.segments.map((s) => (
          <Box key={s.key} px={3} py={2.5} borderRadius="12px" bg={mineral.surfaceAlt} border={`1px solid ${mineral.line}`}>
            <Text fontSize="11.5px" color={mineral.ink3} truncate>{segShort(s)}</Text>
            <Text fontFamily={fonts.display} fontWeight={700} fontSize="17px" color={mineral.ink}>{fmtHours(s.thresholdHours)}</Text>
          </Box>
        ))}
      </Grid>
    </Panel>
  )
}

/* ── Auxiliares ── */
function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

function csv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}
